import { FC, useState, useEffect } from "react";
import CustomModal from "@components/customModel"; // Adjust the import path as needed
import { Button, Col, Input, Row, Select } from "antd";

import {
  ErrorServices,
  useCreateJobGradeMutation,
  useFetchCompanyCountriesQuery,
  useUpdateJobGradeMutation,
} from "@services";
import { ICountry, IJobGrade, IModal } from "@types";
import { showSuccessPopup } from "@utils";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { useFormValidation, useTypedSelector } from "@hooks";
import "@styles/utils.less";

const { Option } = Select;

interface IAddGrade extends IModal {
  selectedJobGrade: IJobGrade | null;
  setSelectedJobGrade: React.Dispatch<React.SetStateAction<IJobGrade | null>>;
}

const AddGrade: FC<IAddGrade> = ({
  isVisible,
  setIsVisible,
  selectedJobGrade,
  setSelectedJobGrade,
}) => {
  const { company, role } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;

  const [jobGrade, setJobGrade] = useState<IJobGrade>({
    grade: "",
    type: null,
    country_ids: [],
  });
  const [isGlobal, setIsGlobal] = useState(false);
  const { data: countries, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });

  const validation = useFormValidation(jobGrade, values => ({
    grade: !values.grade,
    type: !values.type,
    country: !isGlobal && values.country_ids.length === 0,
  }));

  const { data } = countries || {};
  const [createJobGrade, { isLoading: isCreating }] =
    useCreateJobGradeMutation();
  const [updateJobGrade, { isLoading: isUpdating }] =
    useUpdateJobGradeMutation();

  useEffect(() => {
    if (selectedJobGrade) {
      const { grade, type, countries } = selectedJobGrade;
      setJobGrade({
        grade,
        type,
        country_ids: countries?.map(country => country.id),
      });
      if (!countries?.length) {
        setIsGlobal(true);
      }
    }
    return () => {
      setSelectedJobGrade(null);
    };
  }, [selectedJobGrade, setSelectedJobGrade]);

  const handleSubmit = async () => {
    try {
      const job_grade = {
        ...(jobGrade as IJobGrade),
        country_ids: isGlobal ? [] : jobGrade?.country_ids,
      };
      if (selectedJobGrade) {
        await editJobGrade(job_grade);
      } else {
        await addJobGrade(job_grade);
      }
      setIsVisible(false);
      showSuccessPopup({
        title: selectedJobGrade ? "Job Grade Updated" : "New Job Grade Created",
        desc: `You have successfully ${
          selectedJobGrade ? "updated the" : "created new"
        } job grade.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const addJobGrade = async (jobGrade: IJobGrade) =>
    await createJobGrade({
      company_id,
      body: { ...jobGrade },
    }).unwrap();

  const editJobGrade = async (jobGrade: IJobGrade) =>
    await updateJobGrade({
      company_id,
      id: selectedJobGrade?.id,
      body: { ...jobGrade },
    }).unwrap();

  return (
    <CustomModal
      width="w-6/12"
      isVisible={isVisible}
      title={`${selectedJobGrade ? "Update" : "Create"} Job Grade`}
      onClose={() => setIsVisible(false)}
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>
          <button
            className={`bg-revampDarkBlue text-white font-medium text-lg py-2 px-14 rounded-md ${
              !validation.isValid || isCreating || isUpdating
                ? "opacity-50"
                : ""
            }`}
            onClick={handleSubmit}
            disabled={!validation.isValid || isCreating || isUpdating}
          >
            {isCreating || isUpdating
              ? "Loading..."
              : selectedJobGrade
              ? "Update"
              : "Create"}
          </button>
        </>
      }
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grade
            </label>
            <Input
              size="large"
              className={`borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md`}
              name="grade"
              value={jobGrade.grade}
              onBlur={() => validation.touch("grade")}
              onChange={e =>
                setJobGrade(prev => ({ ...prev, grade: e.target.value }))
              }
              placeholder="Enter grade here..."
            />
          </Col>
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Type
            </label>
            <Input
              size="large"
              className={`borderless-input w-full shadow-sm my-2 font-semibold text-sm bg-white px-3 py-3 rounded-md`}
              name="type"
              value={jobGrade.type || ""}
              onBlur={() => validation.touch("type")}
              onChange={e =>
                setJobGrade(prev => ({ ...prev, type: e.target.value }))
              }
              placeholder="Enter type here..."
            />
          </Col>
        </Row>
        <Row className="modal__row">
          <Col span={24}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Countries
            </label>
            <Select
              className="custom-select"
              style={{
                borderColor:"white"
              }}
              disabled={isGlobal}
              size="large"
              showArrow
              placeholder="Select country from here..."
              showSearch={false}
              loading={isFetchingCountries}
              value={jobGrade?.country_ids}
              mode="multiple"
              onBlur={() => validation.touch("country")}
              onChange={val =>
                setJobGrade(prev => ({ ...prev, country_ids: val }))
              }
            >
              {data?.map((country: ICountry) => (
                <Option key={country?.id} value={country?.id}>
                  {country?.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row className="modal__row">
          <Col span={24}>
            {role !== "COMPANY_ADMIN" && (
              <Checkbox
                checked={isGlobal}
                onChange={event => setIsGlobal(event.target.checked)}
              >
                <span
                  className={"font-medium text-lg text-themeInputTitleColor"}
                >
                  Global
                </span>
              </Checkbox>
            )}
          </Col>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddGrade;
